import { isProd } from "../env"
import { getFaro } from "./faro"

/**
 * Detekce pádů appky (WebKit render crash / OOM). Viz IC-2969.
 *
 * Při pádu render procesu (typicky OOM na image/kamera-heavy stránkách) JS
 * kontext okamžitě umře — window.onerror ani Faro se nespustí a nic se nezaloguje.
 * Proto pád detekujeme až při DALŠÍM načtení:
 *
 *  - dokud je stránka viditelná, píšeme do localStorage heartbeat s cleanExit:false,
 *  - při čistém odchodu (pagehide / visibility hidden) přepneme cleanExit:true,
 *  - při dalším loadu: pokud předchozí heartbeat zůstal cleanExit:false a je nedávný,
 *    render proces umřel „za běhu" → nahlásíme jako app_crash_recovered event do Faro.
 *
 * Běžné zavření/navigace/přepnutí do pozadí nastaví cleanExit:true → negenerují false pozitiv.
 */

const STORAGE_KEY = "instacar.crash-heartbeat"
const HEARTBEAT_MS = 10_000
// Starší „nečistý" heartbeat už nemá smysl hlásit (uživatel se vrátil po dlouhé době).
const MAX_STALE_MS = 30 * 60 * 1000

type Heartbeat = {
  ts: number
  cleanExit: boolean
  url: string
  appVersion: string
}

let intervalId: ReturnType<typeof setInterval> | undefined

function readHeartbeat(): Heartbeat | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Heartbeat) : undefined
  } catch {
    return undefined
  }
}

function writeHeartbeat(cleanExit: boolean) {
  try {
    const heartbeat: Heartbeat = {
      ts: Date.now(),
      cleanExit,
      url: window.location.pathname,
      appVersion: process.env.SENTRY_RELEASE ?? "not-set",
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(heartbeat))
  } catch {
    // localStorage nedostupné (private mode apod.) — crash detekci prostě přeskočíme.
  }
}

// Business sessionId je první segment cesty (/{sessionId}/...).
function sessionIdFromUrl(url: string): string {
  return url.split("/").filter(Boolean)[0] ?? ""
}

function reportCrash(previous: Heartbeat) {
  getFaro()?.api.pushEvent("app_crash_recovered", {
    crashedUrl: previous.url,
    crashedSessionId: sessionIdFromUrl(previous.url),
    msSinceLastHeartbeat: String(Date.now() - previous.ts),
    appVersion: previous.appVersion,
  })
}

function startHeartbeat() {
  stopHeartbeat()
  writeHeartbeat(false)
  intervalId = setInterval(() => writeHeartbeat(false), HEARTBEAT_MS)
}

function stopHeartbeat() {
  if (intervalId !== undefined) {
    clearInterval(intervalId)
    intervalId = undefined
  }
}

export function initCrashRecovery() {
  if (!isProd() || !getFaro()) {
    return
  }

  // Detekce pádu z předchozího běhu ještě před tím, než přepíšeme heartbeat.
  const previous = readHeartbeat()
  if (previous && !previous.cleanExit && Date.now() - previous.ts < MAX_STALE_MS) {
    reportCrash(previous)
  }

  startHeartbeat()

  // iOS Safari nespolehlivě střílí pagehide/unload → visibilitychange je nejjistější
  // signál „appka jde do pozadí / pryč" a bereme ho jako čistý odchod.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      stopHeartbeat()
      writeHeartbeat(true)
    } else {
      startHeartbeat()
    }
  })

  window.addEventListener("pagehide", () => {
    stopHeartbeat()
    writeHeartbeat(true)
  })
}
